import React from 'react';
import { AuthService } from '../../../../service/AuthService';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../store/storeNotifications';
import { Namespace } from '../../../../types/namespace';
import type { VectorEmbeddingModel } from '../../../../types/vector';
import { Space } from '../../../../util/Types/Sidebar';
import CreateFormModal from '../../../Forms/CreateFormModal';
import ObjectModal from '../../../Layout/LayoutComponents/FluxObjects/ObjectModal';
import CopySpaceIDModal from '../../../Layout/LayoutComponents/Spaces/CopySpaceIDModal';
import AddToLibraryModal from '../../../Library/AddToLibraryModal';
import AddUsersModal from '../../../Spaces/AddUsersModal';
import UsersAddedModal from '../../../Spaces/UsersAddedModal';
import TemplateSystemModal from '../../../TemplateSystemModal/TemplateSystemModal';
import { DeleteNamespaceDialog } from '../../../UI/DeleteNamespaceDialog';
import { VectorMemoryModal } from '../../../UI/VectorMemoryModal';
import { CreateItemModal } from '../CreateItemModal';
import { SidebarFolderModal } from '../shared/SidebarFolderModal';
import type { useSidebarFolderActions } from '../shared/useSidebarFolderActions';
import type { CreateItemType } from '../types';
import { FluxbotQuickNameDialog } from './FluxbotQuickNameDialog';
import type { useAgentsSidebarHandlers } from './hooks';

type Handlers = ReturnType<typeof useAgentsSidebarHandlers>;
type FolderActions = ReturnType<typeof useSidebarFolderActions>;

interface AgentsSidebarModalsProps {
  spaces: Space[];
  authUserTheme?: string;
  modals: Handlers['modals'];
  handlers: Handlers['handlers'];
  performVectorMemoryDelete: Handlers['performVectorMemoryDelete'];
  setEditingObjectAndClose: Handlers['setEditingObjectAndClose'];
  setEditingVectorMemoryAndClose: Handlers['setEditingVectorMemoryAndClose'];
  folderActions: FolderActions;
  refreshSidebar: () => void;
  resetObjectUpdate: () => void;
  setAuthUser: (user: unknown) => void;
  showTemplatesModal: boolean;
  setShowTemplatesModal: (value: boolean) => void;
  addUsersSpaceId: string | null;
  setAddUsersSpaceId: (value: string | null) => void;
  copySpaceIdModalSpace: { name: string; spaceId: string } | null;
  setCopySpaceIdModalSpace: (
    value: { name: string; spaceId: string } | null,
  ) => void;
  showUsersAddedModal: boolean;
  setShowUsersAddedModal: (value: boolean) => void;
  pendingVectorNamespace: Namespace | null;
  setPendingVectorNamespace: (value: Namespace | null) => void;
  createItemRequest: {
    type: CreateItemType;
    folderId?: string;
    spaceId?: string;
  } | null;
  setCreateItemRequest: (
    value: { type: CreateItemType; folderId?: string; spaceId?: string } | null,
  ) => void;
  vectorCreateRequest: { spaceId?: string } | null;
  setVectorCreateRequest: (value: { spaceId?: string } | null) => void;
  fluxbotCreateRequest: { spaceId?: string } | null;
  setFluxbotCreateRequest: (value: { spaceId?: string } | null) => void;
}

export const AgentsSidebarModals: React.FC<AgentsSidebarModalsProps> = ({
  spaces,
  authUserTheme,
  modals,
  handlers,
  performVectorMemoryDelete,
  setEditingObjectAndClose,
  setEditingVectorMemoryAndClose,
  folderActions,
  refreshSidebar,
  resetObjectUpdate,
  setAuthUser,
  showTemplatesModal,
  setShowTemplatesModal,
  addUsersSpaceId,
  setAddUsersSpaceId,
  copySpaceIdModalSpace,
  setCopySpaceIdModalSpace,
  showUsersAddedModal,
  setShowUsersAddedModal,
  pendingVectorNamespace,
  setPendingVectorNamespace,
  createItemRequest,
  setCreateItemRequest,
  vectorCreateRequest,
  setVectorCreateRequest,
  fluxbotCreateRequest,
  setFluxbotCreateRequest,
}) => {
  const setNotification = useNotificationStore(s => s.setNotification);

  return (
    <>
      {showTemplatesModal && (
        <TemplateSystemModal onClose={() => setShowTemplatesModal(false)} />
      )}
      <ObjectModal
        show={!!modals.editingObject}
        onHide={async () => {
          setEditingObjectAndClose();
          resetObjectUpdate();
          refreshSidebar();
        }}
        spaceId={
          spaces.find(s =>
            s.objects?.some(o => o.id === modals.editingObject?.id),
          )?.id || 'user'
        }
      />
      <VectorMemoryModal
        namespaceId={modals.editingVectorMemory?.id}
        spaceId={
          modals.editingVectorMemory
            ? spaces.find(s =>
                s.namespaces?.some(
                  n => n.id === modals.editingVectorMemory?.id,
                ),
              )?.id
            : undefined
        }
        open={!!modals.editingVectorMemory}
        onOpenChange={async state => {
          if (!state.open) {
            setEditingVectorMemoryAndClose();
            refreshSidebar();
          }
        }}
      />
      {pendingVectorNamespace ? (
        <DeleteNamespaceDialog
          open
          embeddingModelName={
            (pendingVectorNamespace.embeddingModelName ||
              'voyage-3-large') as VectorEmbeddingModel
          }
          id={pendingVectorNamespace.id}
          name={pendingVectorNamespace.name}
          onOpenChange={open => {
            if (!open) setPendingVectorNamespace(null);
          }}
          onNamespaceDelete={({
            embeddingModelName,
            namespaceId,
            namespaceName,
          }) => {
            void performVectorMemoryDelete({
              id: namespaceId,
              name: namespaceName,
              embeddingModelName,
            } as Namespace).then(() => setPendingVectorNamespace(null));
          }}
        />
      ) : null}
      {addUsersSpaceId && (
        <AddUsersModal
          spaceId={addUsersSpaceId}
          onClose={() => setAddUsersSpaceId(null)}
          getCompanySpace={async () => {
            refreshSidebar();
          }}
          setShowUsersAddedModal={setShowUsersAddedModal}
        />
      )}
      {copySpaceIdModalSpace && (
        <CopySpaceIDModal
          spaceName={copySpaceIdModalSpace.name}
          spaceId={copySpaceIdModalSpace.spaceId}
          onClose={() => setCopySpaceIdModalSpace(null)}
        />
      )}
      {showUsersAddedModal && (
        <UsersAddedModal onClose={() => setShowUsersAddedModal(false)} />
      )}
      <CreateFormModal
        flow={modals.createInterfaceFlow?.flow}
        isCreateFormLoading={modals.isCreateFormLoading}
        show={!!modals.createInterfaceFlow}
        onCreateForm={handlers.handleCreateForm}
        onHide={() => modals.setCreateInterfaceFlow(null)}
      />
      <AddToLibraryModal
        flow={modals.libraryFlow?.flow}
        folderId={modals.libraryFlow?.folderId}
        show={!!modals.libraryFlow}
        onHide={() => modals.setLibraryFlow(null)}
        isEditMode={!!modals.libraryFlow?.flow?.library}
        onAddToLibrary={() => {
          setNotification({
            type: NotificationType.Success,
            message: modals.libraryFlow?.flow?.library
              ? 'Library updated successfully'
              : 'Agent added to library successfully',
          });
          modals.setLibraryFlow(null);
          refreshSidebar();
        }}
      />
      <SidebarFolderModal actions={folderActions} theme={authUserTheme} />
      {createItemRequest && (
        <CreateItemModal
          show
          onHide={() => setCreateItemRequest(null)}
          type={createItemRequest.type}
          initialFolderId={createItemRequest.folderId}
          initialSpaceId={createItemRequest.spaceId}
        />
      )}
      {vectorCreateRequest && (
        <VectorMemoryModal
          open
          spaceId={vectorCreateRequest.spaceId}
          onOpenChange={async ({ open }) => {
            if (!open) {
              setVectorCreateRequest(null);
              const userRes = await AuthService.getMe();
              setAuthUser(userRes?.data);
              window.dispatchEvent(new CustomEvent('sidebar-refresh'));
            }
          }}
        />
      )}
      <FluxbotQuickNameDialog
        open={!!fluxbotCreateRequest}
        spaceId={fluxbotCreateRequest?.spaceId}
        onClose={() => setFluxbotCreateRequest(null)}
      />
    </>
  );
};
